import { forwardRef, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

// Formato es-AR: "." miles, "," decimal. raw = string numérica con punto decimal ("1234.5").
export function formatNumberAR(raw, maxDecimals = 4) {
  if (raw === null || raw === undefined || raw === "" || raw === "-") return raw ?? "";
  let s = String(raw).trim().replace(",", ".");
  const neg = s.startsWith("-");
  if (neg) s = s.slice(1);
  const hasDot = s.includes(".");
  let [int = "", dec = ""] = s.split(".");
  int = int.replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  dec = dec.replace(/\D/g, "").slice(0, maxDecimals);
  if (!int && !dec && !hasDot) return neg ? "-" : "";
  const grouped = (int || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${neg ? "-" : ""}${grouped}${hasDot && maxDecimals > 0 ? "," + dec : ""}`;
}

// "1.234,5" -> "1234.5"
export function parseNumberAR(display) {
  if (display === null || display === undefined) return "";
  let s = String(display).replace(/\s/g, "");
  const neg = s.startsWith("-");
  s = s.replace(/-/g, "");
  const i = s.indexOf(",");
  const int = (i >= 0 ? s.slice(0, i) : s).replace(/\D/g, "");
  const dec = i >= 0 ? s.slice(i + 1).replace(/\D/g, "") : null;
  if (!int && dec === null) return neg ? "-" : "";
  return `${neg ? "-" : ""}${int || "0"}${dec !== null ? "." + dec : ""}`;
}

const decimalsFromStep = (step) => {
  if (step === undefined || step === null || step === "any") return 4;
  const s = String(step);
  return s.includes(".") ? s.split(".")[1].length : 0;
};

/**
 * Input numérico que muestra separador de miles mientras se escribe.
 * Emite onChange con un evento sintético cuyo target.value es la cadena
 * numérica cruda con punto decimal ("1234.5"), compatible con los handlers
 * que antes usaban <input type="number">.
 */
const NumberInput = forwardRef(function NumberInput(
  { value, onChange, onFocus, onBlur, decimals, step, min, max, raw = false, className, ...rest },
  ref
) {
  const maxDec = decimals ?? decimalsFromStep(step);
  const allowNeg = !(min !== undefined && min !== null && Number(min) >= 0);
  const [focused, setFocused] = useState(false);
  const [display, setDisplay] = useState(() => formatNumberAR(value, maxDec));
  const innerRef = useRef(null);
  const caretRef = useRef(null);

  const setRef = (el) => {
    innerRef.current = el;
    if (typeof ref === "function") ref(el);
    else if (ref) ref.current = el;
  };

  useEffect(() => {
    if (!focused) { setDisplay(formatNumberAR(value, maxDec)); return; }
    // Enfocado: sólo sincronizar si el padre cambió el valor a algo distinto
    // de lo tipeado (ej. lo limpió o recalculó), sin pisar una coma pendiente.
    const cur = parseNumberAR(display);
    if (value === "" || value === null || value === undefined) {
      if (cur !== "") setDisplay("");
    } else if (!cur.endsWith(".") && cur !== "-" && Number(cur) !== Number(String(value).replace(",", "."))) {
      setDisplay(formatNumberAR(value, maxDec));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, focused, maxDec]);

  useEffect(() => {
    if (caretRef.current === null || !innerRef.current) return;
    try { innerRef.current.setSelectionRange(caretRef.current, caretRef.current); } catch (_) {}
    caretRef.current = null;
  });

  const emit = (e, rawValue) => {
    const el = e.target;
    const target = { value: rawValue, name: el.name, id: el.id, dataset: el.dataset };
    onChange?.({ ...e, target, currentTarget: target, preventDefault() {}, stopPropagation() {} });
  };

  const handleChange = (e) => {
    const el = e.target;
    let typed = el.value;
    let caret = el.selectionStart ?? typed.length;
    // Tecla "." (teclado numérico) se interpreta como coma decimal.
    if (e.nativeEvent?.data === "." && caret > 0 && typed[caret - 1] === ".") {
      typed = typed.slice(0, caret - 1) + "," + typed.slice(caret);
    }
    if (!allowNeg) typed = typed.replace(/-/g, "");
    const rawValue = parseNumberAR(typed);
    const next = formatNumberAR(rawValue, maxDec);
    // Mantener el cursor: misma cantidad de dígitos/coma a la derecha.
    const sigRe = maxDec > 0 ? /[\d,]/ : /\d/;
    const sig = (s) => s.split("").filter((ch) => sigRe.test(ch)).length;
    const rightSig = sig(typed.slice(caret));
    let pos = next.length;
    let count = 0;
    while (pos > 0 && count < rightSig) { pos--; if (sigRe.test(next[pos])) count++; }
    caretRef.current = pos;
    setDisplay(next);
    emit(e, rawValue === "-" ? "" : rawValue);
  };

  const handleFocus = (e) => {
    setFocused(true);
    // Valor 0: seleccionar todo para que lo tipeado lo reemplace.
    if (Number(parseNumberAR(display) || 0) === 0 && display !== "") {
      setTimeout(() => { try { innerRef.current?.select(); } catch (_) {} }, 0);
    }
    onFocus?.(e);
  };
  const handleBlur = (e) => {
    setFocused(false);
    let r = parseNumberAR(display);
    if (r.endsWith(".")) r = r.slice(0, -1);
    if (r === "-") r = "";
    if (r !== parseNumberAR(formatNumberAR(value, maxDec))) emit(e, r);
    onBlur?.(e);
  };

  const Comp = raw ? "input" : Input;
  return (
    <Comp
      ref={setRef}
      type="text"
      inputMode="decimal"
      autoComplete="off"
      className={className}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...rest}
    />
  );
});

export default NumberInput;
